/**
 * Coffee Bean Clicker v2 - AdMob Provider
 * Google AdMob rewarded ads via @capacitor-community/admob
 */

import { Capacitor } from '@capacitor/core';
import type { IAdProvider, AdReward, AdRewardType, AdStatus, AdProviderConfig } from './AdProvider.js';

// Dynamic import type
type AdMobModule = typeof import('@capacitor-community/admob');

export class AdMobProvider implements IAdProvider {
  readonly name = 'AdMobProvider';
  private _status: AdStatus = 'idle';
  private currentReward: AdReward | null = null;
  private config: AdProviderConfig;
  private _isInitialized = false;
  private admobModule: AdMobModule | null = null;

  constructor(config: AdProviderConfig) {
    this.config = config;
  }

  get status(): AdStatus {
    return this._status;
  }

  set status(value: AdStatus) {
    this._status = value;
  }

  async initialize(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      console.log('[AdMobProvider] Not a native platform, skipping');
      return false;
    }

    try {
      this.admobModule = await import('@capacitor-community/admob');
      const { AdMob } = this.admobModule;

      await AdMob.initialize({
        initializeForTesting: this.config.testMode ?? false,
      });

      this._isInitialized = true;
      console.log('[AdMobProvider] Initialized');
      return true;
    } catch (error) {
      console.error('[AdMobProvider] Failed to initialize:', error);
      return false;
    }
  }

  isRewardedAdReady(): boolean {
    return this._status === 'ready';
  }

  async loadRewardedAd(_rewardType?: AdRewardType): Promise<boolean> {
    if (!this._isInitialized || !this.admobModule) {
      console.log('[AdMobProvider] Not initialized');
      return false;
    }

    if (this._status === 'loading') {
      console.log('[AdMobProvider] Already loading');
      return false;
    }

    const platform = Capacitor.getPlatform();
    let adId: string;

    if (platform === 'android') {
      adId = this.config.admobAndroidRewardedAdUnitId
        || 'ca-app-pub-3940256099942544/5224354917'; // Test ad unit
    } else if (platform === 'ios') {
      adId = this.config.admobIosRewardedAdUnitId
        || 'ca-app-pub-3940256099942544/1712485313'; // Test ad unit
    } else {
      console.log('[AdMobProvider] Unsupported platform');
      return false;
    }

    this._status = 'loading';
    console.log('[AdMobProvider] Loading rewarded ad...');

    try {
      const { AdMob } = this.admobModule;
      await AdMob.prepareRewardVideoAd({ adId });
      this._status = 'ready';
      console.log('[AdMobProvider] Rewarded ad ready');
      return true;
    } catch (error) {
      console.error('[AdMobProvider] Failed to load ad:', error);
      this._status = 'error';
      return false;
    }
  }

  async showRewardedAd(_rewardType?: AdRewardType): Promise<AdReward | null> {
    if (!this._isInitialized || !this.admobModule) {
      console.log('[AdMobProvider] Not initialized');
      return null;
    }

    if (this._status !== 'ready') {
      console.log('[AdMobProvider] Ad not ready, loading first...');
      const loaded = await this.loadRewardedAd(_rewardType);
      if (!loaded) return null;
    }

    const { AdMob, RewardAdPluginEvents } = this.admobModule;

    this._status = 'showing';
    console.log('[AdMobProvider] Showing rewarded ad...');

    return new Promise(async (resolve) => {
      let rewarded = false;
      let rewardHandle: { remove: () => Promise<void> } | null = null;
      let dismissHandle: { remove: () => Promise<void> } | null = null;
      let failHandle: { remove: () => Promise<void> } | null = null;

      const cleanup = async () => {
        if (rewardHandle) await rewardHandle.remove();
        if (dismissHandle) await dismissHandle.remove();
        if (failHandle) await failHandle.remove();
      };

      // Reward listener
      rewardHandle = await AdMob.addListener(RewardAdPluginEvents.Rewarded, (_reward) => {
        console.log('[AdMobProvider] User rewarded:', _reward);
        rewarded = true;
      });

      // Dismissed listener
      dismissHandle = await AdMob.addListener(RewardAdPluginEvents.Dismissed, async () => {
        console.log('[AdMobProvider] Ad dismissed, rewarded:', rewarded);
        await cleanup();
        this._status = 'idle';

        if (rewarded && this.currentReward) {
          const reward = this.currentReward;
          this.currentReward = null;
          resolve(reward);
        } else {
          resolve(null);
        }
      });

      // Failed listener
      failHandle = await AdMob.addListener(RewardAdPluginEvents.FailedToShow, async (error) => {
        console.error('[AdMobProvider] Failed to show:', error);
        await cleanup();
        this._status = 'idle';
        resolve(null);
      });

      try {
        await AdMob.showRewardVideoAd();
      } catch (error) {
        console.error('[AdMobProvider] Failed to show ad:', error);
        await cleanup();
        this._status = 'idle';
        resolve(null);
      }
    });
  }

  setReward(reward: AdReward): void {
    this.currentReward = reward;
  }

  dispose(): void {
    this._status = 'idle';
    this.currentReward = null;
    this._isInitialized = false;
    this.admobModule = null;
    console.log('[AdMobProvider] Disposed');
  }
}
